// ============================================
// Toast Notification Component
// ============================================

import { m, AnimatePresence } from 'framer-motion';
import { toast as toastVariants } from '@/animations';
import type { Notification, NotificationType } from '@/types';

// ============================================
// Toast Item
// ============================================

interface ToastItemProps {
  notification: Notification;
  onDismiss: (id: string) => void;
}

function ToastItem({ notification, onDismiss }: ToastItemProps) {
  const { id, message, type } = notification;

  const getIcon = (t: NotificationType) => {
    switch (t) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      case 'info': return 'ℹ️';
    }
  };

  const getBgColor = (t: NotificationType) => {
    switch (t) {
      case 'success': return 'bg-green-500';
      case 'error': return 'bg-red-500';
      case 'warning': return 'bg-amber-500';
      case 'info': return 'bg-blue-500';
    }
  };

  return (
    <m.div
      layout
      variants={toastVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className={`
        flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg
        text-white ${getBgColor(type)}
        cursor-pointer select-none
      `}
      onClick={() => onDismiss(id)}
      role="alert"
      aria-live="polite"
    >
      <span className="text-lg" aria-hidden="true">
        {getIcon(type)}
      </span>
      <span className="font-medium">{message}</span>
    </m.div>
  );
}

// ============================================
// Toast Container
// ============================================

interface ToastContainerProps {
  notifications: Notification[];
  onDismiss: (id: string) => void;
}

export function ToastContainer({ notifications, onDismiss }: ToastContainerProps) {
  return (
    <div
      className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-sm"
      aria-label="Notifications"
    >
      <AnimatePresence mode="popLayout">
        {notifications.map(notification => (
          <ToastItem
            key={notification.id}
            notification={notification}
            onDismiss={onDismiss}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}
